// src/components/wizard/QuestionCard.tsx
import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, FileText, Check, ChevronsUpDown, HelpCircle, ChevronDown } from "lucide-react";
// Direct file uploads are disabled platform-wide — evidence is link-only.
// Legacy uploaded files still render via EvidenceFileItem with signed URL resolution.
import { getSignedUrl } from "@/hooks/useEvidenceStorage";
import { MaturityOption } from "@/components/respondent/MaturityOption";
import { ControlsPills } from "@/components/respondent/ControlsPills";
import { SaveChip } from "@/components/ui/SaveChip";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { domainTheme, type DomainCode } from "@/lib/domainTheme";
import { isEvidenceRequired } from "@/lib/evidenceRules";
import { getQuestionControls } from "@/lib/controls";
import { StandardsPills } from "@/components/respondent/StandardsPills";
import { isSelfAssessmentOnly } from "@/lib/questionnaireType";
import { getTriageQuestionHelp } from "@/lib/questionnaire/triageQuestionHelp";
import { getTriageGuidance } from "@/lib/questionnaire/triageQuestionGuidance";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";


/**
 * Detect whether a URL points to Supabase storage (uploaded file)
 * vs a manually entered external link.
 */
function isStorageUrl(url: string): boolean {
  return url.includes("/storage/v1/object/") || url.includes("/evidence/");
}

/** Extract a human-readable filename from a storage URL or path */
function extractFileName(url: string): string {
  try {
    const decoded = decodeURIComponent(url);
    const lastSegment = decoded.split("/").pop() || "file";
    // Remove the random prefix added by the upload function (e.g. "a1b2c3-")
    const cleaned = lastSegment.replace(/^[a-z0-9]{6}-/i, "");
    // Replace underscores with spaces for readability
    return cleaned.replace(/_/g, " ");
  } catch {
    return "file";
  }
}

/**
 * Extract the storage object key (within the `evidence` bucket) from any of:
 *   - bare path: "userId/assessmentId/qid/abc-file.pdf"
 *   - signed/public/authenticated Supabase URL:
 *       /storage/v1/object/{public|sign|authenticated}/evidence/<key>
 *   - any URL containing "/evidence/<key>"
 */
function extractStoragePath(url: string): string | null {
  if (!url) return null;

  // Bare path (no scheme) → already a storage key
  if (!/^https?:\/\//i.test(url)) return url.replace(/^\/+/, "").split("?")[0];

  // Supabase storage URL forms
  const m = url.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/evidence\/([^?]+)/);
  if (m && m[1]) return decodeURIComponent(m[1]);

  // Generic fallback: anything after "/evidence/"
  const marker = "/evidence/";
  const idx = url.indexOf(marker);
  if (idx >= 0) return decodeURIComponent(url.slice(idx + marker.length).split("?")[0]);

  return null;
}

/** True if URL points at the private Supabase storage host (any project). */
function isSupabaseHostedUrl(url: string): boolean {
  return /^https?:\/\/[^/]+\.supabase\.(co|in)\//i.test(url);
}

/** Small component for rendering a single uploaded file evidence item */
function EvidenceFileItem({ url }: { url: string }) {
  const [downloading, setDownloading] = useState(false);
  const fileName = extractFileName(url);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const storagePath = extractStoragePath(url);
      if (storagePath) {
        const signed = await getSignedUrl(storagePath, 600);
        window.open(signed, "_blank");
        return;
      }
      // No storage path resolvable. Never open a bare Supabase host URL —
      // the private bucket will redirect the browser to the Supabase login
      // page, which appears as "blocked" to the user.
      if (isSupabaseHostedUrl(url)) {
        console.error("[EvidenceFileItem] cannot resolve storage key for Supabase URL:", url);
        alert("This file link is invalid or has expired. Please re-upload the document.");
        return;
      }
      window.open(url, "_blank");
    } catch (err) {
      console.error("[EvidenceFileItem] signed URL error:", err);
      alert("Could not open this file. Please try again or re-upload.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-xs text-foreground truncate font-medium">{fileName}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 shrink-0"
        onClick={handleDownload}
        disabled={downloading}
        title="Download file"
      >
        <Download className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

type Option = {
  label: string;
  score: number;        // 0..100 (we do NOT display this to respondents)
  description?: string; // long description
};

export type QuestionCardProps = {
  assessmentId: string;                                // <= add this
  questionId: number;
  questionKey?: string;
  title: string;
  text: string;
  value: number | null;
  comment: string;
  evidenceLinks: string[];
  evidenceOptions?: string[];
  /** Phase 2.9.2 — Per-service-model additive evidence groups (Telecom only). */
  serviceModelEvidenceGroups?: { model: string; items: string[] }[];
  /** NEW: let parent pass a custom scale so we can show long descriptions */
  scaleOptions?: Option[];
  /** NEW: current domain for color theming */
  currentDomain?: string;
  /** Question data for extracting controls */
  question?: any;
  /** Question number for display */
  questionNumber?: number;
  /** Questionnaire ID for conditional UI logic */
  questionnaireId?: string;
  /** Read-only mode (for portfolio managers) */
  readOnly?: boolean;
  /** Reviewer score (for PM view) */
  reviewerScore?: number | null;
  /** Reviewer notes (for PM view) */
  reviewerNotes?: string | null;
  /** Whether this card has a validation error (missing evidence) */
  hasValidationError?: boolean;
  onChange: (score: number | null, comment: string, evidenceLinks?: string[]) => void;
  onEvidenceUpdate: (links: string[]) => void;
};

/** Single-select searchable dropdown used for triage profile fields like Country. */
function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  options: Option[];
  value: number | null;
  onChange: (score: number) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.score === value) ?? null;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          )}
        >
          <span className={cn(!selected && "text-muted-foreground")}>
            {selected ? selected.label : (placeholder ?? "Select…")}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.label}
                  value={`${opt.label} ${opt.description ?? ""}`}
                  onSelect={() => {
                    onChange(opt.score);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === opt.score ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm">{opt.label}</span>
                    {opt.description && (
                      <span className="text-xs text-muted-foreground">
                        {opt.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function QuestionCard(props: QuestionCardProps) {
  const {
    assessmentId,            // <= add this
    questionId,
    questionKey,
    title,
    text,
    value,
    comment,
    evidenceLinks,
    evidenceOptions = [],
    serviceModelEvidenceGroups = [],
    scaleOptions,
    currentDomain,
    question,
    questionNumber,
    questionnaireId,
    readOnly = false,
    reviewerScore,
    reviewerNotes,
    hasValidationError = false,
    onChange,
    onEvidenceUpdate,
  } = props;

  if (typeof questionId !== "number" || Number.isNaN(questionId)) {
    console.warn("[QuestionCard] Invalid questionId — skipping render", { questionId, props });
    return null;
  }

  // Check if this is a self-assessment questionnaire (no evidence/comments needed)
  const isSelfAssessment = isSelfAssessmentOnly(questionnaireId);

  // Local UI state
  const [localScore, setLocalScore] = useState<number | null>(value ?? null);
  const [localComment, setLocalComment] = useState<string>(comment ?? "");
  const [newEvidenceUrl, setNewEvidenceUrl] = useState<string>("");
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidencePinned, setEvidencePinned] = useState<"auto" | "manual">("auto");
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  
  // Save feedback
  const { state, begin, succeed, fail } = useAutosaveIndicator(250, 1800);
  
  // Evidence refs and state
  const evidenceRef = useRef<HTMLDivElement | null>(null);
  const hasEvidence = Array.isArray(evidenceLinks) && evidenceLinks.length > 0;
  const showEvidenceCallout = isEvidenceRequired(localScore) && !hasEvidence;

  // Default 5-point scale (if parent didn't pass one)
  const fallbackScale: Option[] = useMemo(
    () => [
      { label: "Nonexistent",        score: 0,   description: "Control not present or implemented." },
      { label: "Initial",            score: 25,  description: "Ad hoc or reactive approach." },
      { label: "Developing",         score: 50,  description: "Documented and partially implemented." },
      { label: "Defined/Managed",    score: 75,  description: "Consistently implemented and measured." },
      { label: "Optimized",          score: 100, description: "Continuously improved and optimized." },
    ],
    []
  );
  
  // Sort options for telecom_self_v1 to ensure Low → High order
  const sortedScale: Option[] = useMemo(() => {
    const options = scaleOptions && scaleOptions.length ? scaleOptions : fallbackScale;
    
    // NIST 5-level maturity ordering
    const nistOrder = ["Nonexistent", "Initial", "Developing", "Defined/Managed", "Optimized"];
    if (options.every(opt => nistOrder.includes(opt.label))) {
      return nistOrder
        .map(label => options.find(opt => opt.label === label))
        .filter((opt): opt is Option => opt !== undefined);
    }

    // HRV 3-level risk ordering (Low → Moderate → High)
    const hrvOrder = ["Low Risk", "Moderate Risk", "High Risk"];
    if (options.every(opt => hrvOrder.includes(opt.label))) {
      return hrvOrder
        .map(label => options.find(opt => opt.label === label))
        .filter((opt): opt is Option => opt !== undefined);
    }

    // Fallback: sort by score ascending
    return [...options].sort((a, b) => a.score - b.score);
  }, [scaleOptions, fallbackScale]);
  
  const scale: Option[] = sortedScale;

  // Auto-open/close evidence based on score threshold
  useEffect(() => {
    const required = isEvidenceRequired(localScore);

    if (required && !hasEvidence) {
      // Auto-open if needed and not pinned manually closed
      setEvidenceOpen(true);
      // leave evidencePinned as "auto" so future changes can still auto-close if they drop below 50
    } else if (!required) {
      // Only auto-close if we opened it automatically
      if (evidencePinned === "auto" && !hasEvidence) {
        setEvidenceOpen(false);
      }
    }
  }, [localScore, hasEvidence, evidencePinned]);

  // Smooth scroll when evidence opens - disabled to prevent auto-scroll on domain switch
  // useEffect(() => {
  //   if (evidenceOpen && evidenceRef.current) {
  //     evidenceRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  //   }
  // }, [evidenceOpen]);

  const handleScoreChange = useCallback(
    async (val: string) => {
      if (readOnly) return;
      const next = val === "" ? null : Number(val);
      setLocalScore(next);
      begin();
      try {
        onChange(next, localComment, evidenceLinks);
        succeed();
      } catch (e) {
        console.error(e);
        fail();
      }
    },
    [localComment, evidenceLinks, onChange, begin, succeed, fail, readOnly]
  );

  const handleMaturitySelect = useCallback(
    async (optionScore: string) => {
      if (readOnly) return;
      const score = Number(optionScore);
      setLocalScore(score);
      begin();
      try {
        onChange(score, localComment, evidenceLinks);
        succeed();

        // If they chose ≥50% and have no evidence, ensure the panel is open
        if (isEvidenceRequired(score) && !hasEvidence) {
          setEvidenceOpen(true);
          setEvidencePinned("auto"); // allow auto-close if they drop later
        }
      } catch (e) {
        console.error(e);
        fail();
      }
    },
    [localComment, evidenceLinks, onChange, begin, succeed, fail, hasEvidence, readOnly]
  );

  const handleCommentBlur = useCallback(() => {
    onChange(localScore, localComment, evidenceLinks);
  }, [localScore, localComment, evidenceLinks, onChange]);

  const handleAddEvidence = useCallback(() => {
    const url = newEvidenceUrl.trim();
    if (!url) return;
    const next = [...(evidenceLinks ?? []), url];
    onEvidenceUpdate(next);
    setNewEvidenceUrl("");
  }, [newEvidenceUrl, evidenceLinks, onEvidenceUpdate]);

  const handleRemoveEvidence = useCallback(
    (index: number) => {
      const next = (evidenceLinks ?? []).filter((_, i) => i !== index);
      onEvidenceUpdate(next);
    },
    [evidenceLinks, onEvidenceUpdate]
  );

  const toggleEvidence = useCallback(() => {
    setEvidenceOpen((prev) => !prev);
    setEvidencePinned("manual"); // user took control
  }, []);

  // Get domain color
  const domainColor = currentDomain && currentDomain in domainTheme
    ? domainTheme[currentDomain as DomainCode]?.solid 
    : "#0f172a"; // fallback

  // Get controls for this question
  const controls = getQuestionControls(question);

  return (
    <div className={`rounded-xl border shadow-sm ${hasValidationError ? "border-destructive ring-2 ring-destructive/30 bg-destructive/5" : "bg-white border-border"}`}>
      <div className="h-1 rounded-t-xl" style={{ backgroundColor: domainColor }} />
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-slate-900">
            {questionNumber ? `${questionNumber}. ${title}` : title}
          </h2>
          <p className="mt-1 text-slate-600">{text}</p>
          {(() => {
            const helpText = getTriageQuestionHelp(questionKey);
            return helpText ? (
              <p className="mt-1 text-xs italic text-slate-500 leading-relaxed">
                {helpText}
              </p>
            ) : null;
          })()}
          {(() => {
            const guidance = getTriageGuidance(questionKey);
            if (!guidance) return null;
            return (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setGuidanceOpen((v) => !v)}
                  aria-expanded={guidanceOpen}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                  {guidanceOpen ? "Hide guidance" : "What should I consider?"}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      guidanceOpen && "rotate-180"
                    )}
                  />
                </button>
                {guidanceOpen && (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-2">
                    <div>
                      <div className="font-semibold text-slate-900">What this means</div>
                      <p className="leading-relaxed">{guidance.meaning}</p>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">What to look for</div>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {guidance.lookFor.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">How to answer</div>
                      <ul className="space-y-0.5">
                        <li><span className="font-medium text-emerald-700">Low:</span> {guidance.low}</li>
                        <li><span className="font-medium text-amber-700">Medium:</span> {guidance.medium}</li>
                        <li><span className="font-medium text-red-700">High:</span> {guidance.high}</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <StandardsPills standards={question?.standards} />
        </div>

        {/* Validation error indicator */}
        {hasValidationError && (
          <p className="text-sm font-medium text-destructive">
            {value == null
              ? "⚠ Please select an answer for this question."
              : "⚠ Evidence, link, or comments required for selected maturity level."}
          </p>
        )}

        {/* Controls Pills */}
        <ControlsPills controls={controls} currentDomain={currentDomain} />

        {/* Scale renderer — dropdown for selected profile fields, otherwise stacked cards */}
        {(() => {
          // Profile fields rendered as single-select dropdowns
          const useSearchableDropdown = questionKey === "TPT_P1_COUNTRY";
          const usePlainDropdown =
            questionKey === "TPT_P4_SUBSECTOR" || questionKey === "TPT_P5_YEARS";

          if (useSearchableDropdown || usePlainDropdown) {
            const selectedOpt = scale.find((o) => o.score === localScore) ?? null;

            return (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {readOnly ? "Selected option" : "Select one option"}
                </Label>
                {!readOnly && <div className="mt-1"><SaveChip state={state} /></div>}

                {useSearchableDropdown ? (
                  <SearchableSelect
                    options={scale}
                    value={localScore}
                    disabled={readOnly}
                    placeholder="Search and select…"
                    onChange={(score) => handleMaturitySelect(String(score))}
                  />
                ) : (
                  <Select
                    value={localScore != null ? String(localScore) : ""}
                    disabled={readOnly}
                    onValueChange={(v) => handleMaturitySelect(v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select one option" />
                    </SelectTrigger>
                    <SelectContent>
                      {scale.map((opt) => (
                        <SelectItem key={opt.label} value={String(opt.score)}>
                          {opt.label}
                          {opt.description ? (
                            <span className="ml-2 text-xs text-muted-foreground">
                              — {opt.description}
                            </span>
                          ) : null}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {selectedOpt?.description && (
                  <p className="text-xs text-slate-500 mt-1">{selectedOpt.description}</p>
                )}
              </div>
            );
          }

          // Default: stacked maturity / risk option cards (single-select)
          return (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                {readOnly ? "Selected option" : "Select one option"}
              </Label>
              {!readOnly && <div className="mt-1"><SaveChip state={state} /></div>}
              <div className="space-y-2" role="radiogroup">
                {scale.map((opt) => (
                  <MaturityOption
                    key={opt.label}
                    selected={localScore === opt.score}
                    onSelect={readOnly ? undefined : () => handleMaturitySelect(String(opt.score))}
                    label={opt.label}
                    description={opt.description || opt.label}
                    disabled={readOnly}
                  />
                ))}
              </div>
            </div>
          );
        })()}

        {/* Comment (hidden for self-assessment and read-only) */}
        {!isSelfAssessment && !readOnly && (
          <div className="space-y-2">
            <Label htmlFor={`comment-${questionId}`} className="text-xs text-muted-foreground">
              Comment (optional)
            </Label>
            <Textarea
              id={`comment-${questionId}`}
              placeholder="Add context or rationale…"
              value={localComment}
              onChange={(e) => setLocalComment(e.target.value)}
              onBlur={handleCommentBlur}
            />
          </div>
        )}
        
        {/* Show existing comment in read-only mode */}
        {!isSelfAssessment && readOnly && localComment && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Comment</Label>
            <p className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">{localComment}</p>
          </div>
        )}

        {/* Reviewer Notes Panel (for PM view) */}
        {readOnly && reviewerNotes && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-2">
              Reviewer Notes
            </div>
            <div className="text-sm whitespace-pre-wrap text-blue-900 dark:text-blue-100">
              {reviewerNotes}
            </div>
            {reviewerScore != null && (
              <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                Reviewer Score: {reviewerScore}%
              </div>
            )}
          </div>
        )}

        {/* Evidence callout when required (hide for self-assessment and read-only) */}
        {!isSelfAssessment && !readOnly && showEvidenceCallout && (
          <p className="text-sm text-red-600 mt-1">
            {questionnaireId === "telecom_v2"
              ? "Please provide evidence or a comment to support this response."
              : "Please provide evidence to support this response."}
          </p>
        )}

        {/* Evidence toggle + count row (hide for self-assessment) */}
        {!isSelfAssessment && (
          <div className="mt-3 flex items-center justify-between">
            {!readOnly && (
              <button
                type="button"
                onClick={toggleEvidence}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm hover:bg-slate-50"
              >
                {evidenceOpen ? "Hide evidence" : "Attach evidence"}
              </button>
            )}
            {readOnly && hasEvidence && (
              <button
                type="button"
                onClick={toggleEvidence}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm hover:bg-slate-50"
              >
                {evidenceOpen ? "Hide evidence" : "View evidence"}
              </button>
            )}
            {hasEvidence && (
              <span className="text-xs text-slate-500">
                {evidenceLinks!.length} file{evidenceLinks!.length > 1 ? "s" : ""} attached
              </span>
            )}
          </div>
        )}

        {/* Evidence panel with smooth transitions (hide for self-assessment) */}
        {!isSelfAssessment && (
          <div
            ref={evidenceRef}
            className={`overflow-hidden transition-[max-height] duration-200 ease-in-out ${evidenceOpen ? "max-h-[800px] mt-2" : "max-h-0"}`}
          >
            <div className="rounded-xl border bg-white p-3 space-y-3">
              {/* Guidance (hide in read-only) */}
              {!readOnly && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  Provide a secure link to supporting documentation from your organization's approved
                  document repository, such as SharePoint, OneDrive, Google Drive, Box, or an internal
                  GRC system. Do not upload sensitive files directly to CSV Pro.
                </div>
              )}
              {!readOnly && evidenceOptions.length > 0 && (
                <div className="text-xs">
                  <div className="text-muted-foreground mb-1">Core Evidence Examples:</div>
                  <ul className="list-disc pl-4 space-y-1">
                    {evidenceOptions.map((it, i) => (
                      <li key={`${questionId}-guidance-${i}`} className="text-muted-foreground">
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!readOnly && serviceModelEvidenceGroups.length > 0 && (() => {
                const coreLower = new Set(evidenceOptions.map((s) => s.trim().toLowerCase()));
                return serviceModelEvidenceGroups.map((group) => {
                  const items = group.items.filter(
                    (it) => !coreLower.has(it.trim().toLowerCase())
                  );
                  if (items.length === 0) return null;
                  return (
                    <div key={`${questionId}-sm-${group.model}`} className="text-xs">
                      <div className="text-muted-foreground mb-1">
                        Additional Evidence for {group.model}:
                      </div>
                      <ul className="list-disc pl-4 space-y-1">
                        {items.map((it, i) => (
                          <li
                            key={`${questionId}-sm-${group.model}-${i}`}
                            className="text-muted-foreground"
                          >
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                });
              })()}

              {/* Existing links */}
              {evidenceLinks && evidenceLinks.length > 0 ? (
                <ul className="space-y-2">
                  {evidenceLinks.map((url, idx) => (
                    <li
                      key={`${questionId}-e-${idx}`}
                      className="flex items-center justify-between gap-3 border rounded-md p-2"
                    >
                      {isStorageUrl(url) ? (
                        <EvidenceFileItem url={url} />
                      ) : (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-xs text-primary underline break-all min-w-0"
                        >
                          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          {url}
                        </a>
                      )}
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveEvidence(idx)}
                        >
                          Remove
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-muted-foreground">No evidence added yet.</div>
              )}

              {/* Quick add URL + Upload control (hide in read-only) */}
              {/* Quick add URL (direct uploads disabled — use external secure links only) */}
              {!readOnly && (
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    className="flex-1 h-9 rounded-md border px-3 text-sm bg-background"
                    placeholder="Paste a secure link (SharePoint, OneDrive, Google Drive, Box…)"
                    value={newEvidenceUrl}
                    onChange={(e) => setNewEvidenceUrl(e.target.value)}
                  />
                  <Button type="button" onClick={handleAddEvidence}>
                    Add Link
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}